import { Drawer, LinearProgress } from '@material-ui/core';
import * as React from 'react';
import { Button } from '../../../components/button/Button';
import { FlyoutFooter } from '../../../components/flyout-components/FlyoutFooter';
import { ShipmentStatusMetadata } from '../../models/enums/shipment-status';
import { ShipmentSummary } from '../../models/shipments/shipment-summary';
import { getFailureMessage, ShipmentBulkActivity } from '../../shipment-bulk-activities';

// #region Component Props

interface IProps {
    isOpen      : boolean;
    onClose     : (refresh : boolean) => void;
    shipments   : ShipmentSummary[];
    activity?   : ShipmentBulkActivity;
}

type RowState = 'pending' | 'running' | 'done' | 'failed' | 'skipped';

interface IActivityRow {
    shipment    : ShipmentSummary;
    state       : RowState;
    message?    : string;
}

// #endregion

/**
 * Does an activity for each of the shipments that were selected in a list, one after the other and as soon as it is
 * opened, and shows how it went for every one of them.  The shipments do not depend on each other, so one that fails
 * does not stop the rest.
 */
export const ShipmentBulkActivityFlyout = (props: IProps) => {

    const [rows, setRows]               = React.useState<IActivityRow[]>([]);
    const [isRunning, setIsRunning]     = React.useState(false);
    const [finishNote, setFinishNote]   = React.useState<{ message : string, failed : boolean }>();
    const isMounted                     = React.useRef(true);

    React.useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false; };
    }, []);

    React.useEffect(() => {
        if (props.isOpen && props.activity) {
            const activity = props.activity;
            const list = props.shipments.map((shipment : ShipmentSummary) => {
                const reason = activity.skipReason ? activity.skipReason(shipment) : undefined;
                return { shipment, state: reason ? 'skipped' : 'pending', message: reason } as IActivityRow;
            });

            setRows(list);
            setFinishNote(undefined);
            run(list, activity);
        }
    }, [props.isOpen]);

    /**
     * Do the activity for each of the rows that are waiting, which are marked as they are worked on
     */
    const run = async (list : IActivityRow[], activity : ShipmentBulkActivity) => {

        const show = () => {
            if (isMounted.current) {
                setRows([...list]);
            }
        };

        setIsRunning(true);

        for (const row of list) {

            if (row.state !== 'pending') {
                continue;
            }

            row.state = 'running';
            show();

            try {
                row.message = (await activity.process(row.shipment)) || undefined;
                row.state = 'done';
            }
            catch (err) {
                console.error(err);
                row.message = getFailureMessage(err);
                row.state = 'failed';
            }

            show();
        }

        // What is done for all of them, once each of them was gone through
        let note : { message : string, failed : boolean } | undefined;
        if (activity.finish && list.some((row : IActivityRow) => row.state === 'done')) {
            try {
                const message = await activity.finish();
                note = message ? { message, failed: false } : undefined;
            }
            catch (err) {
                console.error(err);
                note = { message: getFailureMessage(err), failed: true };
            }
        }

        if (isMounted.current) {
            setFinishNote(note);
            setIsRunning(false);
        }
    };

    const retryFailed = () => {
        if (!props.activity) {
            return;
        }

        const list = rows.map((row : IActivityRow) =>
            row.state === 'failed' ? { ...row, state: 'pending', message: undefined } as IActivityRow : row
        );

        setRows(list);
        setFinishNote(undefined);
        run(list, props.activity);
    };

    const count = (state : RowState) => rows.filter((row : IActivityRow) => row.state === state).length;

    // What was done to the shipments is changed for the list when it is looked at again
    const closeFlyout = () => {
        if (isRunning) {
            return;
        }

        props.onClose(!!props.activity?.changesShipments && count('done') > 0);
    };

    const getIndicator = (row : IActivityRow) => {
        switch (row.state) {
            case 'running':
                return <LinearProgress
                    classes={{
                        colorPrimary: 'btn-progress-indicator-primary',
                        barColorPrimary: 'btn-progress-indicator-barcolor'
                    }} />;
            case 'done':
                return <i className="la la-check-circle success" title="Done" />;
            case 'failed':
                return <i className="la la-exclamation-circle error" title="Failed" />;
            case 'skipped':
                return <i className="la la-minus-circle noop" title="Skipped" />;
            default:
                return <i className="la la-clock-o pending" title="Pending" />;
        }
    };

    const getSummary = () => {
        const parts = [`${ count('done') } of ${ rows.length } done`];
        if (count('failed') > 0) {
            parts.push(`${ count('failed') } failed`);
        }
        if (count('skipped') > 0) {
            parts.push(`${ count('skipped') } skipped`);
        }
        return parts.join(', ');
    };

    return (
        <Drawer
            anchor="right"
            onClose={ closeFlyout }
            open={ props.isOpen }>
            <div className="stop-action-drawer large shipment-bulk-flyout">
                <div className="row">
                    <div className="col">
                        <h1>{ props.activity?.title }</h1>
                    </div>
                </div>

                <div className="row">
                    <div className="col">
                        <p className="bulk-activity-summary">{ getSummary() }</p>
                        {
                            finishNote
                                ? <p className={ finishNote.failed ? 'error' : 'success' } data-testid="bulk-finish-note">
                                    { finishNote.failed ? `Could not combine: ${ finishNote.message }` : `Downloaded ${ finishNote.message }` }
                                </p>
                                : null
                        }
                    </div>
                </div>

                <div className="row">
                    <div className="col">
                        <table className="table table-striped">
                            <thead>
                                <tr>
                                    <th className="text-center">ID</th>
                                    <th className="text-center">Status</th>
                                    <th className="text-center">Name</th>
                                    <th className="text-center">Activity</th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    rows.map((row : IActivityRow) =>
                                        <tr key={`bulk-row-${ row.shipment.id }`}
                                            data-testid={`bulk-row-${ row.shipment.id }`}
                                            data-state={ row.state }>
                                            <td className="text-center">{ row.shipment.id }</td>
                                            <td className="text-center">
                                                <span className={ "text-uppercase shipment-status-color " + ShipmentStatusMetadata[row.shipment.status].class }>
                                                    { ShipmentStatusMetadata[row.shipment.status].display }
                                                </span>
                                            </td>
                                            <td className="text-center">{ row.shipment.name }</td>
                                            <td>
                                                <div className="bulk-activity-cell">
                                                    <div className="bulk-activity-indicator">{ getIndicator(row) }</div>
                                                    <span className="bulk-activity-message">{ row.message }</span>
                                                </div>
                                            </td>
                                        </tr>
                                    )
                                }
                            </tbody>
                        </table>
                    </div>
                </div>

                <FlyoutFooter>
                    <div className="row">
                        <div className="col">
                            {
                                !isRunning && count('failed') > 0
                                    ? <Button
                                        text="Retry Failed"
                                        classes="attention"
                                        testID="retry-bulk-activity-flyout"
                                        onBtnClick={ retryFailed } />
                                    : null
                            }
                            <Button
                                text="Close"
                                classes="action"
                                disable={ isRunning }
                                testID="close-bulk-activity-flyout"
                                onBtnClick={ closeFlyout } />
                        </div>
                    </div>
                </FlyoutFooter>

            </div>
        </Drawer>
    );
};
